
import { Button } from "@/components/ui/button";
import { Mail, UserPlus, Users, TrendingUp, Activity, Sparkles, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

interface TeamHeaderProps {
  isAdmin: boolean;
  onAddTeam: () => void;
}

export const TeamHeader = ({ isAdmin, onAddTeam }: TeamHeaderProps) => {
  const { toast } = useToast();
  const { t } = useLanguage();

  const handleEmailToGuards = async () => {
    try {
      // Fetch all profiles with guard role
      const { data: guardProfiles, error } = await supabase
        .from('profiles')
        .select('email')
        .eq('Role', 'guard')
        .neq('status', 'inactive');
        
      if (error) {
        console.error("Error fetching guard emails:", error);
        toast({
          variant: "destructive",
          title: t('error'),
          description: t('couldNotFetchEmails')
        });
        return;
      }
      
      if (!guardProfiles || guardProfiles.length === 0) {
        toast({
          title: t('noGuardsFound'),
          description: t('noActiveGuardsFound')
        });
        return;
      }
      
      // Extract emails from the response
      const emails = guardProfiles
        .map(profile => profile.email)
        .filter(email => email) // Remove null/undefined values
        .join(',');
        
      if (!emails.length) {
        toast({
          title: t('noEmailsFound'),
          description: t('noGuardsWithEmails')
        });
        return;
      }
      
      // Open default email client with pre-populated email addresses
      window.location.href = `mailto:${emails}?subject=Message to All Guards`;
    } catch (err) {
      console.error("Error handling email to guards:", err);
        toast({
          variant: "destructive",
          title: t('error'),
          description: "An unexpected error occurred"
        });
    }
  };

  return (
    <div className="space-y-6">
      {/* Enhanced Header Section */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="p-4 bg-gradient-to-br from-primary to-primary/80 rounded-2xl shadow-luxury animate-pulse">
            <Users className="h-8 w-8 text-primary-foreground" />
          </div>
          <div className="h-8 w-px bg-gradient-to-b from-transparent via-primary/50 to-transparent"></div>
          <div className="p-4 bg-gradient-to-br from-primary to-primary/80 rounded-2xl shadow-luxury animate-pulse" style={{ animationDelay: '0.5s' }}>
            <Activity className="h-8 w-8 text-primary-foreground" />
          </div>
        </div>
        
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r from-primary via-primary/80 to-primary bg-clip-text text-transparent leading-tight">
          {t('teamManagement')}
        </h1>
        
        <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto font-medium">
          {isAdmin ? t('manageAllTeams') : t('viewYourTeams')}
        </p>
        
        {/* Quick Stats */}
        <div className="flex flex-wrap items-center justify-center gap-6 pt-4">
          <div className="flex items-center gap-2 px-4 py-2 bg-background/80 backdrop-blur-sm rounded-full border border-primary/20 shadow-sm">
            <TrendingUp className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">{t('teamAnalytics')}</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-background/80 backdrop-blur-sm rounded-full border border-primary/20 shadow-sm">
            <Activity className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">{t('realTimeMonitoring')}</span>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/10 to-blue-500/10 backdrop-blur-sm rounded-full border border-primary/30 shadow-sm">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">{t('administrator')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced Action Buttons */}
      {isAdmin && (
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
          <Button 
            className="w-full sm:w-auto bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all duration-300 hover-scale"
            onClick={onAddTeam}
          >
            <UserPlus className="mr-2 h-4 w-4" />
            {t('addNewTeam')}
          </Button>
          
          <Button 
            variant="outline"
            className="w-full sm:w-auto bg-background/80 backdrop-blur-sm border-red-500/20 hover:border-red-500/40 text-red-600 hover:bg-red-50 transition-all duration-300 hover-scale"
            onClick={handleEmailToGuards}
          >
            <Mail className="mr-2 h-4 w-4" />
            {t('emailToGuards')}
          </Button>
        </div>
      )}
    </div>
  );
};
